import * as React from "react";
import { useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Box,
  IconButton,
  TableSortLabel,
  Pagination,
} from "@mui/material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { useTheme } from "@mui/material/styles";

const DataTable = ({
  columns = [],
  data = [],
  totalRecords = 0,
  page = 0,
  rowsPerPage = 20,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onFilterChange,
  sortConfig,
}) => {
  const handleSort = React.useCallback(
    (column) => {
      if (sortConfig?.key === column && sortConfig.direction === "desc") {
        onSortChange?.(null); // Reset sorting
      } else {
        const direction =
          sortConfig?.key === column && sortConfig.direction === "asc"
            ? "desc"
            : "asc";
        onSortChange?.({ key: column, direction });
      }
    },
    [onSortChange, sortConfig]
  );

  const sortedData = React.useMemo(() => {
    if (!sortConfig?.key) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      return sortConfig.direction === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [data, sortConfig]);
  const theme = useTheme();
  return (
    <>
      <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
        <Table stickyHeader>
          <TableHead >
            <TableRow >
              {columns.map((col) => (
                <TableCell
                  key={col}
                  align="center"
                  sx={{
                    backgroundColor: "primary.dark",
                    color: "white",
                    fontSize: 16,
                    
                  }} 
                >
                  <TableSortLabel
                    active={sortConfig?.key === col}
                    direction={
                      sortConfig?.key === col ? sortConfig.direction : "asc"
                    }
                    onClick={() => handleSort(col)}
                  >
                    {col}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedData.length > 0 ? (
              sortedData.map((row, idx) => (
                <TableRow key={idx}>
                  {columns.map((col) => (
                    <TableCell key={col} align="center">
                      {row[col]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  No Data Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        gap={2}
        mt={2}
      >
        <Box>
          Rows per page:&nbsp;
          <select
            value={rowsPerPage}
            onChange={(e) => onRowsPerPageChange?.(e)}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              backgroundColor: theme.palette.background.paper, // or theme.palette.bg_light.main
              color: theme.palette.text.primary,
              border: `1px solid ${theme.palette.divider}`,
              fontSize: "0.875rem",
              minWidth: 80,
            }}
          >
            {[20, 50, 100, 200].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </Box>

        <Pagination
          count={Math.ceil(totalRecords / rowsPerPage)}
          page={page + 1}
          onChange={(e, newPage) => onPageChange?.(e, newPage - 1)}
          color="primary"
          showFirstButton
          showLastButton
          siblingCount={3}
          boundaryCount={1}
          size="medium"
          sx={{
            alignSelf: { xs: "center", md: "flex-end" },
            flexWrap: "wrap",
          }}
        />
      </Box>
    </>
  );
};

export default DataTable;
